import { useCallback, useEffect } from 'react';

import { FormField } from '@/_components';
import type { AdvanceFilter } from '@/_containers';
import { FilterApplyTrigger, FilterTypeSelector, getFilterKey, useFiltersState } from '@/_containers';
import type { ContactQuerySchema } from '@/_pages/contacts/shared';
import { FormContactsAsyncMultiSelect } from '@/_pages/contacts/shared/components';
import { TeamMembersAsyncMultiSelect } from '@/_pages/team-members/shared';
import type { TeamMemberQuerySchema } from '@/_pages/team-members/shared/schemas';
import { nameResolver, snackbar } from '@/_shared';

import { FormSupportPriorityMultiSelect, FormSupportStatusMultiSelect } from '../../shared/components';
import type { SupportFilterSchema } from '../../shared/filters';
import { useSupportFormContext } from '../../shared/form-context';
import { priorityOptions, supportStatusOptions } from '../../shared/schemas/support-query-schema';

type ObjectWithValue = { value: string; label: string };
type FilterObject = TeamMemberQuerySchema | ContactQuerySchema | ObjectWithValue;

// Helper functions to clean up the logic
const extractFilterData = (objects: FilterObject[], type: 'agent' | 'contact' | 'status' | 'priority') => {
  if (!Array.isArray(objects)) return { values: [], labels: [] };

  if (type === 'agent') {
    const values = objects.map((obj) => (obj as TeamMemberQuerySchema).freshdesk?.toString()).filter(Boolean);
    const labels = objects
      .map((obj) => {
        const member = obj as TeamMemberQuerySchema;
        return `${member.first_name} ${member.last_name}`.trim();
      })
      .filter(Boolean);
    return { values, labels };
  }

  if (type === 'contact') {
    const values = objects.map((obj) => (obj as ContactQuerySchema).freshdesk?.toString()).filter(Boolean);
    const labels = objects
      .map((obj) => {
        const contact = obj as ContactQuerySchema;
        return nameResolver(contact.first_name, contact.last_name);
      })
      .filter(Boolean);
    return { values, labels };
  }

  // status and priority
  const values = objects.map((obj) => (obj as ObjectWithValue).value).filter(Boolean);
  const labels = objects.map((obj) => (obj as ObjectWithValue).label).filter(Boolean);
  return { values, labels };
};

const getBackendFieldName = (mainKey: string): string => {
  const keyMapping: Record<string, string> = {
    agent: 'agent.freshdesk',
    contact: 'requester.freshdesk',
  };
  return keyMapping[mainKey] || mainKey;
};

const getSelectedOptions = (fieldValue: unknown, options: { value: string; label: string }[]) => {
  return options.filter((opt) =>
    Array.isArray(fieldValue) ? fieldValue.some((v: { value: string }) => v.value === opt.value) : false
  );
};

export function CustomSupportFilterWithRadios({
  handleSubmitAdvFilters,
  ...perFilter
}: AdvanceFilter<SupportFilterSchema> & {
  handleSubmitAdvFilters: (v: AdvanceFilter<SupportFilterSchema>) => void;
}) {
  const { mainKey, subKey, subKey2, types, selectedType, applied } = perFilter;
  const form = useSupportFormContext();

  const {
    typeState: [type, setType],
    transition: [isPending, startTransition],
    inputValueRequired,
  } = useFiltersState(null as string | null, types, selectedType);

  const isCustomField = mainKey === 'agent' || mainKey === 'contact' || mainKey === 'status' || mainKey === 'priority';

  const handleSubmit = useCallback(() => {
    if (!isCustomField) {
      console.warn('not custom field');
      return;
    }

    if (type === null || typeof type === 'undefined' || type === '') {
      snackbar.error('Please select a filter type');
      return;
    }

    if (!form.getValues(mainKey) && inputValueRequired) {
      snackbar.error('Please select a value');
      return;
    }

    const formValueAll = form.getValues(mainKey);
    const selectedType = types.find((f) => f.value === type) ?? { name: '', value: '' };

    // Get the form values as objects for custom fields
    const isCustomMultiSelect = ['agent', 'contact', 'status', 'priority'].includes(mainKey);
    const formObjects = isCustomMultiSelect ? (Array.isArray(formValueAll) ? formValueAll : []) : [];

    let filterValues: string[] = [];
    let filterLabels: string[] = [];

    if (isCustomMultiSelect) {
      const { values, labels } = extractFilterData(
        formObjects as FilterObject[],
        mainKey as 'agent' | 'contact' | 'status' | 'priority'
      );
      filterValues = values.filter((v): v is string => typeof v === 'string');
      filterLabels = labels.filter((l): l is string => typeof l === 'string');
    } else {
      // Handle nested values for non-custom fields
      let value = '';
      if (subKey && typeof formValueAll === 'object' && formValueAll !== null && !Array.isArray(formValueAll)) {
        const formObj = formValueAll as Record<string, unknown>;
        if (subKey2) {
          const nestedValue = formObj[subKey];
          if (typeof nestedValue === 'object' && nestedValue !== null && !Array.isArray(nestedValue)) {
            const nestedObj = nestedValue as Record<string, unknown>;
            value = (nestedObj[subKey2] ?? '').toString();
          }
        } else {
          value = (formObj[subKey] ?? '').toString();
        }
      } else {
        value = (formValueAll ?? '').toString();
      }
      filterValues = [value];
      filterLabels = [value];
    }

    const backendKey = getBackendFieldName(mainKey as string);
    const key = getFilterKey(backendKey, subKey, subKey2, selectedType?.absoluteKey);

    // filterValue should be ||| separated labels, filter should be comma-separated IDs/values
    const filterValue = filterLabels.join('|||');
    const filter = `${type}[${key}]=${filterValues.join(',')}`;

    startTransition(() => {
      handleSubmitAdvFilters({
        ...perFilter,
        applied: true,
        filterValue,
        valueToApply: filter,
        selectedType,
      });
    });
  }, [
    isCustomField,
    type,
    form,
    mainKey,
    inputValueRequired,
    subKey,
    subKey2,
    startTransition,
    handleSubmitAdvFilters,
    perFilter,
    types,
  ]);

  useEffect(() => {
    if (!applied) {
      const key = mainKey;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      form.setValue(key, []);
      setType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  if (!isCustomField) {
    console.warn('not custom field');
    return null;
  }

  return (
    <section className={'flex flex-col'}>
      {inputValueRequired && (
        <div className="mb-4">
          {mainKey === 'status' && (
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => {
                const options = supportStatusOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }));
                const selectedOptions = getSelectedOptions(field.value, options);
                return (
                  <FormSupportStatusMultiSelect
                    value={selectedOptions}
                    onChange={(opts) => field.onChange(opts)}
                    formItemClassName="w-full"
                    menuListClassName="h-32"
                  />
                );
              }}
            />
          )}
          {mainKey === 'priority' && (
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => {
                const options = priorityOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }));
                const selectedOptions = getSelectedOptions(field.value, options);
                return (
                  <FormSupportPriorityMultiSelect
                    value={selectedOptions}
                    onChange={(opts) => field.onChange(opts)}
                    formItemClassName="w-full"
                    menuListClassName="h-32"
                  />
                );
              }}
            />
          )}
          {mainKey === 'agent' && (
            <FormField
              control={form.control}
              name="agent"
              render={({ field }) => (
                <TeamMembersAsyncMultiSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  formItemClassName="w-full"
                  menuListClassName="h-32"
                />
              )}
            />
          )}
          {mainKey === 'contact' && (
            <FormField
              control={form.control}
              name="contact"
              render={({ field }) => (
                <FormContactsAsyncMultiSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  formItemClassName="w-full"
                  menuListClassName="h-32"
                />
              )}
            />
          )}
        </div>
      )}
      <FilterTypeSelector types={types} type={type} setType={setType} />

      <FilterApplyTrigger handleSubmit={handleSubmit} isPending={isPending} />
    </section>
  );
}
